import React, { useState, useEffect } from 'react';
import './Questionnaire.css';
import { StepVideo } from '../shared/components';
import { useStepMedia } from '../shared/hooks';

// API Configuration - Always use production URLs (Vercel/Render)
const PRODUCTION_API = 'https://meal-planner-app-mve2.onrender.com';
const API_BASE = process.env.REACT_APP_API_URL || PRODUCTION_API;

// Map step numbers to step keys for media
const STEP_TO_MEDIA_KEY = {
  1: 'CUISINES',
  2: 'SERVINGS',
  3: 'INGREDIENTS',
  4: 'DIETARY',
  5: 'MEALS',
  6: null, // Days step doesn't have media
};

const DAYS_OF_WEEK = [
  { id: 'Monday', icon: '📅', label: 'Monday' },
  { id: 'Tuesday', icon: '📅', label: 'Tuesday' },
  { id: 'Wednesday', icon: '📅', label: 'Wednesday' },
  { id: 'Thursday', icon: '📅', label: 'Thursday' },
  { id: 'Friday', icon: '📅', label: 'Friday' },
  { id: 'Saturday', icon: '🎉', label: 'Saturday' },
  { id: 'Sunday', icon: '🎉', label: 'Sunday' }
];

// Emoji icons for dietary preferences
const DIETARY_ICONS = {
  diabetic: '🩺',
  dairyFree: '🥛',
  glutenFree: '🌾',
  peanutFree: '🥜',
  vegetarian: '🥗',
  kosher: '✡️',
  vegan: '🌱',
  lowCarb: '🥬',
  keto: '🥑',
  paleo: '🦴'
};

function Questionnaire({ user, onSubmit, onLogout, selectedStores }) {
  // Wizard step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;

  // Step media for videos/posters
  const { getMedia, hasMedia } = useStepMedia();

  // Dynamic options loaded from API
  const [cuisineOptions, setCuisineOptions] = useState([]);
  const [dietaryOptionsData, setDietaryOptionsData] = useState([]);

  // User selections
  const [cuisines, setCuisines] = useState([]);
  const [numberOfPeople, setNumberOfPeople] = useState(2);
  const [servingsByMeal, setServingsByMeal] = useState({
    breakfast: 2,
    lunch: 2,
    dinner: 2
  });
  const [meals, setMeals] = useState({
    breakfast: false,
    lunch: false,
    dinner: false
  });
  const [selectedDays, setSelectedDays] = useState({
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true,
    Sunday: true
  });
  const [dietaryPreferences, setDietaryPreferences] = useState({});
  const [leftovers, setLeftovers] = useState(['']);
  const [specialOccasion, setSpecialOccasion] = useState(false);
  const [specialIngredient, setSpecialIngredient] = useState('');
  const [specialOptions, setSpecialOptions] = useState([]);
  const [specialLoading, setSpecialLoading] = useState(false);
  const [specialError, setSpecialError] = useState('');
  const [specialMealChoice, setSpecialMealChoice] = useState(null);
  const [errors, setErrors] = useState({});

  // Load cuisine and dietary options from API on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Load cuisines
        const cuisinesResponse = await fetch(`${API_BASE}/api/cuisines`);
        if (cuisinesResponse.ok) {
          const cuisinesData = await cuisinesResponse.json();
          setCuisineOptions(cuisinesData.cuisines.map(c => c.name));
        }

        // Load dietary options
        const dietaryResponse = await fetch(`${API_BASE}/api/dietary-options`);
        if (dietaryResponse.ok) {
          const dietaryData = await dietaryResponse.json();
          setDietaryOptionsData(dietaryData.options);

          // Initialize dietary preferences state with all options set to false
          const initialDietaryState = {};
          dietaryData.options.forEach(option => {
            initialDietaryState[option.key] = false;
          });
          setDietaryPreferences(initialDietaryState);
        }
      } catch (error) {
        console.error('Error loading options:', error);
        // Fail silently - will use empty arrays
      }
    };

    loadOptions();
  }, []);

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE}/api/user/preferences`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });

        if (response.ok) {
          const data = await response.json();
          const prefs = data.preferences;

          if (prefs) {
            // Pre-fill cuisines
            if (prefs.default_cuisines && prefs.default_cuisines.length > 0) {
              setCuisines(prefs.default_cuisines);
            }

            // Pre-fill number of people
            if (prefs.default_people) {
              setNumberOfPeople(prefs.default_people);
            }

            // Pre-fill meals (convert array to object)
            if (prefs.default_meals && prefs.default_meals.length > 0) {
              const mealsObj = {
                breakfast: prefs.default_meals.includes('breakfast'),
                lunch: prefs.default_meals.includes('lunch'),
                dinner: prefs.default_meals.includes('dinner')
              };
              setMeals(mealsObj);
            }

            // Pre-fill days (convert array to object)
            if (prefs.default_days && prefs.default_days.length > 0) {
              const daysObj = {
                Monday: prefs.default_days.includes('Monday'),
                Tuesday: prefs.default_days.includes('Tuesday'),
                Wednesday: prefs.default_days.includes('Wednesday'),
                Thursday: prefs.default_days.includes('Thursday'),
                Friday: prefs.default_days.includes('Friday'),
                Saturday: prefs.default_days.includes('Saturday'),
                Sunday: prefs.default_days.includes('Sunday')
              };
              setSelectedDays(daysObj);
            }

            // Pre-fill dietary preferences (convert array to object)
            if (prefs.default_dietary && prefs.default_dietary.length > 0) {
              setDietaryPreferences(prev => {
                const updated = {...prev};
                prefs.default_dietary.forEach(key => {
                  if (key in updated) {
                    updated[key] = true;
                  }
                });
                return updated;
              });
            }

            console.log('✅ Preferences loaded and applied');
          }
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
        // Fail silently - user can still fill out the form manually
      }
    };

    loadPreferences();
  }, []);

  const toggleCuisine = (cuisine) => {
    setCuisines(prev => {
      if (prev.includes(cuisine)) {
        return prev.filter(c => c !== cuisine);
      } else {
        return [...prev, cuisine];
      }
    });
  };

  const toggleMeal = (mealType) => {
    setMeals(prev => ({
      ...prev,
      [mealType]: !prev[mealType]
    }));
  };

  const toggleDay = (day) => {
    setSelectedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const toggleDietaryPreference = (preference) => {
    setDietaryPreferences(prev => ({
      ...prev,
      [preference]: !prev[preference]
    }));
  };

  const handleAddLeftoverField = () => {
    setLeftovers([...leftovers, '']);
  };

  const handleLeftoverChange = (index, value) => {
    const newLeftovers = [...leftovers];
    newLeftovers[index] = value;
    setLeftovers(newLeftovers);
  };

  const handleRemoveLeftoverField = (index) => {
    const newLeftovers = leftovers.filter((_, i) => i !== index);
    setLeftovers(newLeftovers.length === 0 ? [''] : newLeftovers);
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (cuisines.length === 0) {
        newErrors.cuisines = 'Please select at least one cuisine';
      }
    } else if (step === 5) {
      if (!meals.breakfast && !meals.lunch && !meals.dinner) {
        newErrors.meals = 'Please select at least one meal type';
      }
    } else if (step === 6) {
      const hasSelectedDay = Object.values(selectedDays).some(day => day === true);
      if (!hasSelectedDay) {
        newErrors.days = 'Please select at least one day';
      }
    }
    // Steps 2, 3, 4 are optional or always valid

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(totalSteps, prev + 1));
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
    setErrors({});
  };

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      const selectedMeals = Object.keys(meals).filter(meal => meals[meal]);
      const selectedDietaryPreferences = Object.keys(dietaryPreferences).filter(pref => dietaryPreferences[pref]);
      const daysArray = Object.keys(selectedDays).filter(day => selectedDays[day]);
      const leftoverIngredients = leftovers.filter(item => item.trim() !== '');

      // Only include servings for selected meals
      const servingsForSelectedMeals = {};
      selectedMeals.forEach(meal => {
        servingsForSelectedMeals[meal] = servingsByMeal[meal];
      });

      console.log('📅 Selected days being submitted:', daysArray);
      console.log('🍽️ Selected meals being submitted:', selectedMeals);
      console.log('🍽️ Servings by meal:', servingsForSelectedMeals);

      onSubmit({
        cuisines,
        people: numberOfPeople,
        selectedMeals: selectedMeals,  // Send array of meal types: ['breakfast', 'lunch', 'dinner']
        servingsByMeal: servingsForSelectedMeals,  // Send object: {breakfast: 1, dinner: 3}
        selectedDays: daysArray,  // Send array of days: ['Monday', 'Tuesday', etc.]
        dietaryPreferences: selectedDietaryPreferences,  // Send array: ['diabetic', 'dairyFree', etc.]
        leftovers: leftoverIngredients,  // Send array of leftover ingredients
        specialOccasion: specialOccasion,  // Include special occasion flag
        specialIngredient: specialOccasion ? specialIngredient.trim() : null,  // Primary ingredient
        specialMealChoice: specialOccasion ? specialMealChoice : null  // Selected meal option
      });
    }
  };

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return 'What cuisines do you enjoy?';
      case 2: return 'How many people are you feeding?';
      case 3: return 'Got leftovers or ingredients to use up?';
      case 4: return 'Any dietary restrictions?';
      case 5: return 'Which meals do you need?';
      case 6: return 'Which days do you want to plan for?';
      default: return '';
    }
  };

  const getStepSubtitle = () => {
    switch(currentStep) {
      case 1: return 'Select all that apply';
      case 2: return 'We\'ll adjust portions accordingly';
      case 3: return 'Add ingredients you\'d like to incorporate into your meals (optional)';
      case 4: return 'Select all that apply (optional)';
      case 5: return 'Select meals and set number of servings for each';
      case 6: return 'Select the days you need meal plans for';
      default: return '';
    }
  };

  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return (
          <>
            <div className="cuisine-grid">
              {cuisineOptions.map(cuisine => (
                <button
                  key={cuisine}
                  className={`cuisine-chip ${cuisines.includes(cuisine) ? 'selected' : ''}`}
                  onClick={() => toggleCuisine(cuisine)}
                >
                  {cuisine}
                </button>
              ))}
            </div>
            {errors.cuisines && <p className="error">{errors.cuisines}</p>}
          </>
        );

      case 2:
        return (
          <div className="number-selector">
            <button
              className="number-btn"
              onClick={() => setNumberOfPeople(Math.max(1, numberOfPeople - 1))}
            >
              −
            </button>
            <span className="number-display">{numberOfPeople}</span>
            <button
              className="number-btn"
              onClick={() => setNumberOfPeople(Math.min(12, numberOfPeople + 1))}
            >
              +
            </button>
          </div>
        );

      case 3:
        return (
          <>
            <div className="leftovers-inputs">
              {leftovers.map((item, index) => (
                <div key={index} className="leftover-input-row">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleLeftoverChange(index, e.target.value)}
                    placeholder="e.g., 'chicken', 'broccoli', 'rice'"
                    className="leftover-input"
                  />
                  {leftovers.length > 1 && (
                    <button
                      onClick={() => handleRemoveLeftoverField(index)}
                      className="remove-leftover-btn"
                      title="Remove this ingredient"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={handleAddLeftoverField} className="add-leftover-btn">
              + Add Another Ingredient
            </button>
          </>
        );

      case 4:
        return (
          <div className="meal-checkboxes">
            {dietaryOptionsData.map(option => (
              <label key={option.key} className={`meal-checkbox ${dietaryPreferences[option.key] ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={dietaryPreferences[option.key] || false}
                  onChange={() => toggleDietaryPreference(option.key)}
                />
                <span className="meal-icon">{DIETARY_ICONS[option.key] || '🍽️'}</span>
                <span className="meal-name">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 5:
        return (
          <>
            <div className="meal-checkboxes">
              <label className={`meal-checkbox ${meals.breakfast ? 'checked' : ''}`}>
                <div className="meal-checkbox-left">
                  <input
                    type="checkbox"
                    checked={meals.breakfast}
                    onChange={() => toggleMeal('breakfast')}
                  />
                  <span className="meal-icon">🌅</span>
                  <span className="meal-name">Breakfast</span>
                </div>
                {meals.breakfast && (
                  <div className="servings-control">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setServingsByMeal(prev => ({...prev, breakfast: Math.max(1, prev.breakfast - 1)}));
                      }}
                      className="serving-btn"
                    >−</button>
                    <span className="servings-display">{servingsByMeal.breakfast}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setServingsByMeal(prev => ({...prev, breakfast: Math.min(12, prev.breakfast + 1)}));
                      }}
                      className="serving-btn"
                    >+</button>
                  </div>
                )}
              </label>

              <label className={`meal-checkbox ${meals.lunch ? 'checked' : ''}`}>
                <div className="meal-checkbox-left">
                  <input
                    type="checkbox"
                    checked={meals.lunch}
                    onChange={() => toggleMeal('lunch')}
                  />
                  <span className="meal-icon">☀️</span>
                  <span className="meal-name">Lunch</span>
                </div>
                {meals.lunch && (
                  <div className="servings-control">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setServingsByMeal(prev => ({...prev, lunch: Math.max(1, prev.lunch - 1)}));
                      }}
                      className="serving-btn"
                    >−</button>
                    <span className="servings-display">{servingsByMeal.lunch}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setServingsByMeal(prev => ({...prev, lunch: Math.min(12, prev.lunch + 1)}));
                      }}
                      className="serving-btn"
                    >+</button>
                  </div>
                )}
              </label>

              <label className={`meal-checkbox ${meals.dinner ? 'checked' : ''}`}>
                <div className="meal-checkbox-left">
                  <input
                    type="checkbox"
                    checked={meals.dinner}
                    onChange={() => toggleMeal('dinner')}
                  />
                  <span className="meal-icon">🌙</span>
                  <span className="meal-name">Dinner</span>
                </div>
                {meals.dinner && (
                  <div className="servings-control">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setServingsByMeal(prev => ({...prev, dinner: Math.max(1, prev.dinner - 1)}));
                      }}
                      className="serving-btn"
                    >−</button>
                    <span className="servings-display">{servingsByMeal.dinner}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setServingsByMeal(prev => ({...prev, dinner: Math.min(12, prev.dinner + 1)}));
                      }}
                      className="serving-btn"
                    >+</button>
                  </div>
                )}
              </label>
            </div>

            {/* Special Occasion Meal Toggle */}
            <div className="special-occasion-section">
              <label className={`special-occasion-toggle ${specialOccasion ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={specialOccasion}
                  onChange={(e) => {
                    setSpecialOccasion(e.target.checked);
                    if (!e.target.checked) {
                      setSpecialIngredient('');
                      setSpecialOptions([]);
                      setSpecialMealChoice(null);
                      setSpecialError('');
                    }
                  }}
                />
                <div className="special-occasion-content">
                  <span className="special-occasion-icon">✨</span>
                  <div className="special-occasion-text">
                    <h4>Add a Special Occasion Meal</h4>
                    <p className="special-occasion-desc">
                      Elevate your week with a premium restaurant-quality meal featuring gourmet ingredients from Whole Foods and elevated serving suggestions.
                    </p>
                  </div>
                </div>
              </label>

              {/* Special Occasion Ingredient Input */}
              {specialOccasion && (
                <div className="special-occasion-details" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f5f0', borderRadius: '8px' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Primary Ingredient
                    </label>
                    <input
                      type="text"
                      value={specialIngredient}
                      onChange={(e) => setSpecialIngredient(e.target.value)}
                      placeholder="e.g., lobster, steak, shrimp, truffle"
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <button
                    disabled={!specialIngredient.trim() || specialLoading}
                    onClick={async () => {
                      setSpecialLoading(true);
                      setSpecialError('');
                      try {
                        const token = localStorage.getItem('auth_token');
                        const res = await fetch(`${API_BASE}/api/special-occasion/options`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': token ? `Bearer ${token}` : ''
                          },
                          body: JSON.stringify({ ingredient: specialIngredient.trim() })
                        });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data?.error || data?.message || 'Request failed');
                        setSpecialOptions(Array.isArray(data.options) ? data.options : []);
                      } catch (e) {
                        setSpecialError(String(e.message || e));
                      } finally {
                        setSpecialLoading(false);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      backgroundColor: specialLoading ? '#ccc' : '#d4a574',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: specialLoading || !specialIngredient.trim() ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      marginBottom: '15px'
                    }}
                  >
                    {specialLoading ? 'Generating Options...' : 'Get Meal Options'}
                  </button>

                  {specialError && (
                    <div style={{ color: '#d32f2f', marginBottom: '15px', fontSize: '14px' }}>
                      ❌ {specialError}
                    </div>
                  )}

                  {specialOptions.length > 0 && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Select Your Special Occasion Meal
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {specialOptions.map((opt, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              setSpecialMealChoice(opt);
                            }}
                            style={{
                              padding: '12px',
                              backgroundColor: specialMealChoice === opt ? '#d4a574' : '#fff8f3',
                              border: `2px solid ${specialMealChoice === opt ? '#d4a574' : '#e0cfc7'}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.2s'
                            }}
                          >
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                              {specialMealChoice === opt && '✓ '}
                              {opt.title || opt.name || `Option ${idx + 1}`}
                            </div>
                            {opt.notes && (
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                {opt.notes}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {errors.meals && <p className="error">{errors.meals}</p>}
          </>
        );

      case 6:
        return (
          <>
            <div className="days-checkboxes">
              {DAYS_OF_WEEK.map((day) => (
                <label key={day.id} className={`meal-checkbox ${selectedDays[day.id] ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedDays[day.id]}
                    onChange={() => toggleDay(day.id)}
                  />
                  <span className="meal-icon">{day.icon}</span>
                  <span className="meal-name">{day.label}</span>
                </label>
              ))}
            </div>
            {errors.days && <p className="error">{errors.days}</p>}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="questionnaire-page">
      <nav className="navbar">
        <h2>AI Meal Planner</h2>
        <div className="user-info">
          <img src={user.picture} alt={user.name} className="user-avatar" />
          <span>{user.name}</span>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
      </nav>

      <div className="questionnaire-container">
        <h1>Let's Create Your Meal Plan</h1>
        <p className="subtitle">Step {currentStep} of {totalSteps}</p>

        {/* Progress bar */}
        <div className="progress-bar-container">
          <div className="progress-bar" style={{width: `${(currentStep / totalSteps) * 100}%`}}></div>
        </div>

        <div className="question-section wizard-step">
          {/* Step video/poster media */}
          {STEP_TO_MEDIA_KEY[currentStep] && hasMedia(STEP_TO_MEDIA_KEY[currentStep]) && (
            <div className="step-media-container">
              <StepVideo
                videoUrl={getMedia(STEP_TO_MEDIA_KEY[currentStep])?.videoUrl}
                posterUrl={getMedia(STEP_TO_MEDIA_KEY[currentStep])?.posterUrl}
                runRule={getMedia(STEP_TO_MEDIA_KEY[currentStep])?.runRule || 'loop'}
              />
            </div>
          )}

          <h3>{getStepTitle()}</h3>
          <p className="hint">{getStepSubtitle()}</p>
          {renderStepContent()}
        </div>

        {/* Navigation buttons */}
        <div className="wizard-navigation">
          {currentStep > 1 && (
            <button className="wizard-btn wizard-back" onClick={handleBack}>
              ← Back
            </button>
          )}

          {currentStep < totalSteps ? (
            <button className="wizard-btn wizard-next" onClick={handleNext}>
              Next →
            </button>
          ) : (
            <button className="wizard-btn generate-btn" onClick={handleSubmit}>
              Generate My Meal Plan
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Questionnaire;
